import { checkIfMemberExists } from '@api/identity';
import { SERVICES } from '@client/components/pricing/SeasonalPricing/utils/estimateDisplay';
import type { PricingEstimationTypeSource } from '@client/components/pricing/types';
import { XO_SESSION_TOKEN } from '@constants/membership';
import {
	fillFavoriteHeart,
	setSavedVendorId,
} from '@pages/Storefront/containers/vendor_info/components/favorites_button/actions';
import { addConversation } from '@redux/messagedVendors';
import { inquireAPIUrl } from '@settings';
import {
	addVendorsToFavorites,
	upsertWeddingForUser,
} from '@tkmp/rfq/redux/utils';
import { formatPrice } from '@tkmp/rfq/utils';
import { determineReferredMarketCode } from '@utils';
import { getPlatform } from '@utils/getPlatform';
import { Logger } from '@utils/logger/logger';
import { getMember } from '@xo-union/store-membership-redux';
import type { SavedVendor } from 'apps/marketplace-web/client/contexts/WeddingsContext/types';
import { CookieStorage } from 'cookie-storage';
import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import request from 'superagent';
import { cookieGetItem } from '../../../../client/utils/cookie/consentManagementCookie';
import { VENDOR_REFERRED_MARKET_CODE } from '../../../../constants/cookies';
import { getVisitId, getVisitorId } from '../../../../utils/cookies';
import { guestCountToRange } from '../../utils/guestCountToRange';
import { autoCreateAccount, autoLogIn } from '../../utils/userLogIn';
import type {
	EstimateDetails,
	EstimateDetailsService,
	EstimateDetailsSource,
	EstimateDetailsUnavailableService,
	InquiryEstimateBreakdown,
	PricingInquiryInput,
	PricingInquiryResult,
} from './types';

function escapeHtml(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

const handleSoftLogin = async ({
	email,
	firstName,
	lastName,
	weddingDate,
	guestCount,
	member,
	dispatch,
}: {
	email: string;
	firstName: string;
	lastName: string;
	weddingDate: string | undefined;
	guestCount: number;
	member: Membership.Member | null | undefined;
	dispatch: Redux.Dispatch;
}): Promise<{ isExistingUser: boolean }> => {
	let isExistingUser = false;
	if (!member) {
		let isUserRegistered = false;
		try {
			isUserRegistered = await checkIfMemberExists(email);
		} catch (error) {
			Logger.error({
				log_message: 'Pricing inquiry identity check failed',
				errorToLog: error,
				newRelic: true,
			});
			throw error;
		}
		isExistingUser = isUserRegistered;
		if (!isUserRegistered) {
			const accountCreated = await dispatch(
				autoCreateAccount({
					email,
					firstName,
					lastName,
					weddingDate: weddingDate ?? '',
					guestCount,
				}),
			);
			if (accountCreated) {
				await dispatch(autoLogIn(email));
			}
		} else {
			// Existing user: silently restore their session from the cookie so
			// the inquiry and favorites can be associated to their account.
			try {
				await dispatch(getMember());
			} catch (error) {
				Logger.error({
					log_message: 'Soft login session restore failed',
					errorToLog: error,
					newRelic: true,
				});
			}
		}
	}
	return { isExistingUser };
};

const persistProfileWeddingData = async ({
	member,
	guestCount,
	weddingDate,
}: {
	member: Membership.Member;
	guestCount: number;
	weddingDate: string | undefined;
}) => {
	const token = new CookieStorage().getItem(XO_SESSION_TOKEN) || null;
	if (!token) {
		Logger.warn({
			log_message:
				'Pricing inquiry profile persistence skipped: missing session token',
		});
		return;
	}

	const guestRange = guestCountToRange(guestCount);
	const nextWeddingDate = weddingDate ?? member.wedding_date ?? null;
	const nextGuestRange = guestRange;
	const shouldUpdateWeddingDate =
		!!weddingDate && member.wedding_date !== weddingDate;
	const shouldUpdateGuestCount = member.number_of_guests !== nextGuestRange;

	if (!shouldUpdateWeddingDate && !shouldUpdateGuestCount) {
		return;
	}

	try {
		await upsertWeddingForUser({
			memberUuid: member.id,
			token,
			weddingDate: nextWeddingDate,
			guestCount: nextGuestRange,
		});
	} catch (error) {
		Logger.warn({
			log_message: `Pricing inquiry profile persistence failed: ${error instanceof Error ? error.message : String(error)}`,
		});
	}
};

// VENDOR (vendor FAQ pricing) maps to vendorPrice; AI maps to aiCostBreakdown; LOCAL_MARKET/MARKET (unused today) fall back to faq.
const SOURCE_BY_DATA_SOURCE: Record<
	Exclude<PricingEstimationTypeSource, null>,
	EstimateDetailsSource
> = {
	VENDOR: 'vendorPrice',
	AI: 'aiCostBreakdown',
	LOCAL_MARKET: 'faq',
	MARKET: 'faq',
};

// Missing dataSource (e.g. season-range collapse in transform.ts drops it) has always meant vendor-provided in the message body; default to vendorPrice to match.
const resolveEstimateDetailsSource = (
	dataSource: PricingEstimationTypeSource | undefined,
): EstimateDetailsSource =>
	dataSource ? SOURCE_BY_DATA_SOURCE[dataSource] : 'vendorPrice';

const resolveNonSpecificWeddingDate = (
	weddingDate: string | undefined,
	weddingDateForProfile: string | undefined,
): string | undefined => {
	if (weddingDateForProfile || !weddingDate) return undefined;
	const match = weddingDate.match(/^(\d{4})-(\d{2})-\d{2}$/);
	return match ? `${match[2]}/${match[1]}` : undefined;
};

const buildEstimateDetails = ({
	estimate,
	selectedServices,
	unavailableServices,
	startingPrice,
	weddingDate,
	weddingDateForProfile,
}: {
	estimate: InquiryEstimateBreakdown | null;
	selectedServices: PricingInquiryInput['selectedServices'];
	unavailableServices: PricingInquiryInput['unavailableServices'];
	startingPrice: number | null;
	weddingDate: string | undefined;
	weddingDateForProfile: string | undefined;
}): EstimateDetails | null => {
	if (!estimate) return null;

	// Both flags true or both false mean season is undefined (matches pricing-estimation transform).
	const season: 'peak' | 'offPeak' | undefined =
		estimate.isPeakSeason === estimate.isOffPeakSeason
			? undefined
			: estimate.isPeakSeason
				? 'peak'
				: 'offPeak';

	const selectedServiceDetails: EstimateDetailsService[] = [];
	const unavailableServiceDetails: EstimateDetailsUnavailableService[] = [];

	for (const { key, value: name } of SERVICES) {
		const isUnavailable = unavailableServices.includes(key);
		if (isUnavailable) {
			unavailableServiceDetails.push({ key, name });
		}

		if (!selectedServices.includes(key)) continue;

		if (isUnavailable) {
			selectedServiceDetails.push({
				key,
				name,
				isPerGuest: false,
				pricing: { value: 0, source: resolveEstimateDetailsSource(undefined) },
			});
			continue;
		}

		const est = estimate[key];
		if (!est) continue;
		const value = est.cost ?? est.minCost;
		if (value == null) continue;

		selectedServiceDetails.push({
			key,
			name,
			isPerGuest: est.unit === 'PER_GUEST',
			pricing: {
				value,
				source: resolveEstimateDetailsSource(est.dataSource),
				...(season ? { season } : {}),
			},
		});
	}

	const { totalCost } = estimate;
	const nonSpecificWeddingDate = resolveNonSpecificWeddingDate(
		weddingDate,
		weddingDateForProfile,
	);

	return {
		selectedServices: selectedServiceDetails,
		unavailableServices: unavailableServiceDetails,
		...(nonSpecificWeddingDate
			? { weddingDetails: { nonSpecificWeddingDate } }
			: {}),
		totalPricing: {
			...(typeof totalCost.originalMinCost === 'number' &&
			Number.isFinite(totalCost.originalMinCost)
				? { realEstimate: totalCost.originalMinCost }
				: {}),
			minBoundary: totalCost.minCost,
			maxBoundary: totalCost.maxCost,
			startingPrice: startingPrice ?? 0,
			startingPriceAdjust: !!totalCost.isBelowMinimumSpend,
		},
	};
};

const buildSenderInfoData = ({
	email,
	firstName,
	lastName,
	guestCount,
	weddingDate,
	phoneNumber,
	estimateDetails,
}: {
	email: string;
	firstName: string;
	lastName: string;
	guestCount: number;
	weddingDate: string | undefined;
	phoneNumber: string | undefined;
	estimateDetails: EstimateDetails | null;
}) => {
	// The API treats counts above 300 as "301+" with no upper bound (maxGuestCount = 0).
	const GUEST_COUNT_OVERFLOW_THRESHOLD = 301;
	const [minGuestCount, maxGuestCount] =
		guestCount >= GUEST_COUNT_OVERFLOW_THRESHOLD
			? [GUEST_COUNT_OVERFLOW_THRESHOLD, 0]
			: [guestCount, guestCount];
	return {
		details: {
			flexibleWeddingDate: false,
			...(estimateDetails ? { estimateDetails } : {}),
		},
		isEstimate: true,
		email,
		maxGuestCount,
		minGuestCount,
		name: {
			first: firstName,
			last: lastName,
		},
		weddingDate,
		...(phoneNumber ? { phone: phoneNumber } : {}),
	};
};

const buildInquiryData = ({
	messageBody,
	email,
	firstName,
	lastName,
	storefrontId,
	guestCount,
	weddingDate,
	vendor,
	referredMarketCode,
	visitIds,
	anonymousId,
	currentMember,
	senderInfoData,
}: {
	messageBody: string;
	email: string;
	firstName: string;
	lastName: string;
	storefrontId: string;
	guestCount: number;
	weddingDate: string | undefined;
	vendor: Vendor.Decorated | null | undefined;
	referredMarketCode: string;
	visitIds: Record<string, string | undefined>;
	anonymousId: string | undefined;
	currentMember: Membership.Member | null | undefined;
	senderInfoData: ReturnType<typeof buildSenderInfoData>;
}) => ({
	body: messageBody,
	email,
	first_name: firstName,
	last_name: lastName,
	user_email: email,
	storefrontId,
	number_of_guests: guestCount,
	wedding_date: weddingDate,
	isRfq: true,
	isVRT: false,
	leadFormType: 'direct',
	source: {
		application: 'theknot',
		categoryCode: vendor?.categoryCode,
		feature: 'storefront-pricing',
		marketCode: vendor?.marketCode,
		platform: getPlatform(),
	},
	sourceContent: 'storefront-pricing-guided-flow',
	sourcePage: vendor?.isPaid ? 'paid storefront' : 'unpaid storefront',
	tags: ['INBOX'],
	vendorReferredMarketCode: referredMarketCode,
	...visitIds,
	...(anonymousId ? { anonymousId } : {}),
	...(currentMember?.id
		? { senderInfo: { ...senderInfoData, id: currentMember.id } }
		: { senderAnonymousInfo: senderInfoData }),
});

export const useSendPricingInquiry = () => {
	const dispatch = useDispatch();
	const store = useStore<Redux.State>();

	const conversations = useSelector(
		(state: Redux.State) => state.messagedVendors?.conversations,
	);

	const member = useSelector((state: Redux.State) => state.membership?.member);

	const sendInquiry = useCallback(
		async (input: PricingInquiryInput): Promise<PricingInquiryResult> => {
			const {
				guestCount,
				memberInfo,
				storefrontId,
				vendorId,
				weddingDate,
				weddingDateForProfile,
				vendor,
				anonymousId,
			} = input;
			const { email, firstName, lastName, phoneNumber } = memberInfo;

			const existingConversationId = conversations?.[vendorId];
			let isExistingUser = false;

			try {
				// Soft login: for anonymous users, check if an account exists; if not,
				// auto-create one and log in so the lead is associated to a member.
				({ isExistingUser } = await handleSoftLogin({
					email,
					firstName,
					lastName,
					weddingDate,
					guestCount,
					member,
					dispatch,
				}));

				// Re-read member after potential auto-login
				const currentMember = store.getState().membership?.member;
				if (currentMember?.id) {
					await persistProfileWeddingData({
						member: currentMember,
						guestCount,
						weddingDate: weddingDateForProfile,
					});
				}

				const visitId = getVisitId();
				const visitorId = getVisitorId();
				const referredMarketCode = determineReferredMarketCode({
					cookieMarketCode: cookieGetItem(VENDOR_REFERRED_MARKET_CODE),
					isFromSearch: input.isFromSearch,
					isReferred: input.isReferred,
					marketCode: vendor?.marketCode,
				});
				const visitIds = visitId && visitorId ? { visitId, visitorId } : {};
				const dateValue = escapeHtml(
					input.weddingDateDisplay ?? 'Not specified',
				);

				const serviceLines: string[] = [];
				for (const { key, value } of SERVICES) {
					if (!input.selectedServices.includes(key)) continue;

					if (input.unavailableServices.includes(key)) {
						serviceLines.push(`• ${value}: <strong>Not offered</strong>`);
						continue;
					}

					const est = input.estimate?.[key];
					if (!est) continue;
					const cost = est.cost ?? est.minCost;
					if (cost == null) continue;
					const isEstimated =
						est.dataSource != null && est.dataSource !== 'VENDOR';
					const price =
						est.unit === 'PER_GUEST'
							? `${formatPrice(cost)} / guest`
							: formatPrice(cost);
					const line = isEstimated
						? `• ${value}: <strong>${price}</strong> (calculated with local data)`
						: `• ${value}: <strong>From ${price}</strong> (provided by the venue)`;

					serviceLines.push(line);
				}

				const totalCost = input.estimate?.totalCost;
				const totalStr = totalCost
					? totalCost.minCost !== totalCost.maxCost
						? `${formatPrice(totalCost.minCost)} - ${formatPrice(totalCost.maxCost)}`
						: formatPrice(totalCost.minCost)
					: '';

				// When the vendor's starting-cost floor raised the total above the
				// computed estimate, tell the vendor what the couple's original
				// estimate was so they have the context. Mirrors the yellow
				// disclosure the couple sees on the storefront at the same time.
				const minimumSpendDisclaimer =
					totalCost?.isBelowMinimumSpend &&
					typeof totalCost.originalMinCost === 'number' &&
					Number.isFinite(totalCost.originalMinCost)
						? `* Note: The original estimated total of ${formatPrice(totalCost.originalMinCost)} has been adjusted to meet the venue’s required minimum spend of ${formatPrice(totalCost.minCost)}.`
						: '';

				const messageBody = [
					'<strong><i>The following custom cost projection was generated using The Knot’s price estimator.</i></strong>',
					'',
					'The Knot’s price estimator helps couples understand if a venue they’re interested in could work for their budget.',
					'',
					'The Knot used available pricing information and other local data to calculate an estimate based on the following wedding details and requested services.',
					'',
					`Wedding date: <strong>${dateValue}</strong>`,
					`Guest count: <strong>${guestCount}</strong>`,
					`Estimated total price: <strong>${totalStr}${minimumSpendDisclaimer ? '*' : ''}</strong>`,
					...(minimumSpendDisclaimer ? [minimumSpendDisclaimer] : []),
					'',
					'Requested services:',
					'',
					...serviceLines,
					'',
					'This is not intended as exact pricing.',
					'',
					'Reply here to start the conversation about availability and exact pricing.',
				].join('\n');

				const estimateDetails = buildEstimateDetails({
					estimate: input.estimate,
					selectedServices: input.selectedServices,
					unavailableServices: input.unavailableServices,
					startingPrice: input.startingPrice ?? null,
					weddingDate,
					weddingDateForProfile,
				});

				const senderInfoData = buildSenderInfoData({
					email,
					firstName,
					lastName,
					guestCount,
					weddingDate,
					phoneNumber,
					estimateDetails,
				});

				const inquiryData = buildInquiryData({
					messageBody,
					email,
					firstName,
					lastName,
					storefrontId,
					guestCount,
					weddingDate,
					vendor,
					referredMarketCode,
					visitIds,
					anonymousId,
					currentMember,
					senderInfoData,
				});

				const res = await request.post(inquireAPIUrl).send(inquiryData);
				const response = res.body;

				const { id: leadId, conversationId } = response;

				if (conversationId && vendorId) {
					dispatch(addConversation({ [vendorId]: conversationId }));
				}

				if (
					currentMember &&
					vendor &&
					!store.getState().favorites?.savedVendorId
				) {
					const weddingId = store.getState().favorites?.weddingId;
					const settledResults = await addVendorsToFavorites(
						weddingId,
						currentMember,
						[vendor],
					);
					const savedVendor =
						settledResults[0]?.status === 'fulfilled'
							? (
									settledResults[0] as PromiseFulfilledResult<{
										saved_vendor: SavedVendor;
									}>
								).value?.saved_vendor
							: null;
					if (savedVendor?.id) {
						dispatch(fillFavoriteHeart());
						dispatch(setSavedVendorId(savedVendor.id));
					}
				}

				Logger.log({
					log_message: `Pricing inquiry sent successfully - conversationId: ${conversationId}, leadId: ${leadId}, vendorId: ${vendorId}, existingConversation: ${!!existingConversationId}`,
				});

				return {
					status: 'SENT',
					conversationId: conversationId || '',
					messageId: leadId || '',
					sentAt: new Date(),
					isExistingUser,
				};
			} catch (error) {
				Logger.error({
					log_message: 'Pricing inquiry failed',
					errorToLog: error,
					newRelic: true,
				});

				return {
					status: 'FAILED',
					conversationId: existingConversationId || '',
					messageId: '',
					sentAt: new Date(),
					isExistingUser,
					error:
						error instanceof Error
							? error.message
							: 'Failed to send pricing inquiry',
				};
			}
		},
		[conversations, dispatch, member, store],
	);

	return {
		sendInquiry,
	};
};
